export type JsonPrimitive = string | number | boolean | null;
export type MetadataMap = Record<string, string | number | boolean | null>;
export type FieldValueType = string | number | boolean;

export enum PiiLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
  NONE = 'NONE',
}

export enum RedactionFramework {
  GDPR = 'GDPR',
  PDPL = 'PDPL',
  HIPAA = 'HIPAA',
  CCPA = 'CCPA',
  LOCAL = 'LOCAL',
}

export enum DocumentCategoryEnum {
  IDENTITY = 'IDENTITY',
  PASSPORT = 'PASSPORT',
  VISA = 'VISA',
  RESIDENCE_PERMIT = 'RESIDENCE_PERMIT',
  DRIVER_LICENSE = 'DRIVER_LICENSE',
  VEHICLE_REGISTRATION = 'VEHICLE_REGISTRATION',
  LICENSE_PLATE = 'LICENSE_PLATE',
  BIRTH_CERTIFICATE = 'BIRTH_CERTIFICATE',
  MARRIAGE_CERTIFICATE = 'MARRIAGE_CERTIFICATE',
  DEATH_CERTIFICATE = 'DEATH_CERTIFICATE',
  TAX_DOCUMENT = 'TAX_DOCUMENT',
  INVOICE = 'INVOICE',
  BANK_STATEMENT = 'BANK_STATEMENT',
  FINANCIAL_STATEMENT = 'FINANCIAL_STATEMENT',
  UTILITY_BILL = 'UTILITY_BILL',
  BUSINESS_LICENSE = 'BUSINESS_LICENSE',
  LEGAL_CONTRACT = 'LEGAL_CONTRACT',
  POWER_OF_ATTORNEY = 'POWER_OF_ATTORNEY',
  PAYSLIP = 'PAYSLIP',
  EMPLOYMENT_CONTRACT = 'EMPLOYMENT_CONTRACT',
  ACADEMIC_CERTIFICATE = 'ACADEMIC_CERTIFICATE',
  TRANSCRIPT = 'TRANSCRIPT',
  PROPERTY_DEED = 'PROPERTY_DEED',
  LEASE_AGREEMENT = 'LEASE_AGREEMENT',
  INSURANCE_POLICY = 'INSURANCE_POLICY',
  MEDICAL_RECORD = 'MEDICAL_RECORD',
  BILL_OF_LADING = 'BILL_OF_LADING',
  CUSTOMS_DECLARATION = 'CUSTOMS_DECLARATION',
  OTHER_GOVERNMENT_DOC = 'OTHER_GOVERNMENT_DOC',
}

export enum ValidationAlgorithm {
  LUHN = 'LUHN',
  MODULO_11 = 'MODULO_11',
  MODULO_97 = 'MODULO_97',
  ICAO_MRZ_731 = 'ICAO_MRZ_731',
  VIN_ISO_3779 = 'VIN_ISO_3779',
  IBAN_ISO_13616 = 'IBAN_ISO_13616',
  TAX_ID_VALIDATION = 'TAX_ID_VALIDATION',
  NATIONAL_ID_VALIDATION = 'NATIONAL_ID_VALIDATION',
  REGEX_MATCH = 'REGEX_MATCH',
  DATE_VALIDATION = 'DATE_VALIDATION',
}

export enum ReviewTaskStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CORRECTED = 'CORRECTED',
}

export interface UserDto {
  id: string;
  sub: string;
  email: string;
  name?: string;
  picture?: string;
  role: string;
  tenantId: string;
  activeModelId?: string;
  activeModelName?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export interface OidcLoginResponseDto {
  authorizationUrl: string;
  state: string;
}

export interface OidcCallbackRequestDto {
  code: string;
  state: string;
}

export interface AuthResponseDto {
  accessToken: string;
  user: UserDto;
}

export enum PriceModelTypeEnum {
  FLAT_RATE = 'flat_rate',
  TIERED_VOLUME = 'tiered_volume',
  TIERED_GRADUATED = 'tiered_graduated',
  PACKAGE_BASED = 'package_based',
  PAY_AS_GO = 'pay_as_go',
  TIER_PAY_AS_GO = 'tier_pay_as_go',
}

export enum SubscriptionStatusEnum {
  ACTIVE = 'active',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  UNPAID = 'unpaid',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAUSED = 'paused',
}

export interface PriceTierDto {
  up_to: number | null;
  flat_amount?: number;
  unit_amount?: number;
}

export interface PriceModelDto {
  id: string;
  applicationId: string;
  name: string;
  pricingType: PriceModelTypeEnum;
  amount?: number;
  unitAmount?: number;
  currency: string;
  interval?: 'day' | 'week' | 'month' | 'year';
  intervalCount?: number;
  trialPeriodDays?: number;
  quantity?: number;
  tieredPrice?: PriceTierDto[];
  isActive: boolean;
  createdAt: string | Date;
}

export interface SubscriptionDto {
  id: string;
  logtoUserId: string;
  applicationId: string;
  priceModelId: string;
  priceModel?: PriceModelDto;
  status: SubscriptionStatusEnum;
  currentPeriodStart?: string | Date;
  currentPeriodEnd?: string | Date;
  trialStart?: string | Date;
  trialEnd?: string | Date;
  cancelAtPeriodEnd?: boolean;
  canceledAt?: string | Date;
  currency: string;
  createdAt: string | Date;
}

export interface SubscriptionLookupResponseDto {
  hasSubscription: boolean;
  subscription?: SubscriptionDto;
  status?: SubscriptionStatusEnum;
  accessGranted: boolean;
  marketplaceUrl: string;
  message?: string;
}

export interface WalletBalanceDto {
  logtoUserId: string;
  balance: number; // Euros (float up to 6 decimals)
  currency: string;
}

export interface UsageEventDto {
  identifier: string;
  price_id: string;
  logto_user_id: string;
  usage: number;
  timestamp: number;
}

export interface AdjustUsageDto {
  cancel_identifier: string;
  identifier: string;
  price_id: string;
  logto_user_id: string;
  usage: number;
  timestamp: number;
}

export interface UsageEventRecordDto {
  identifier: string;
  status: 'success' | 'failed';
  usage_value: number;
  fail_reason?: string;
  timestamp: number;
}

export interface UsageStatsDto {
  bucket: string;
  total_usages: number;
  total_requests: number;
}

export interface FailedUsageEventDto {
  identifier: string;
  logto_user_id: string;
  price_id: string;
  failed_reason: string;
  timestamp: number;
}

export interface SensifaiWebhookEnvelopeDto {
  id: string;
  event_type: 'customer.balance.depleted' | 'subscription.created' | 'subscription.status.updated' | string;
  created_at: number;
  data: {
    customer_id: string; // Logto User ID
    id?: string;
    price_model_id?: string;
    status?: SubscriptionStatusEnum;
    balance?: number;
    [key: string]: any;
  };
}

export interface CountryDto {
  id?: string;
  iso2: string;
  iso3: string;
  name: string;
  region: 'europe' | 'middle_east' | 'asia' | 'north_america' | 'south_america' | 'oceania';
  languages: string[];
  ocrLanguagePacks: string[];
  supportedDocuments: DocumentCategoryEnum[];
  privacyRegulations: RedactionFramework[];
  governmentAuthority?: string;
  keywords: string[];
  enabled: boolean;
}

export interface JurisdictionDto {
  id: string;
  countryIso2: string;
  name: string;
  code: string;
  regionalLaws: string[];
}

export interface DocumentTypeDto {
  id: string;
  category: DocumentCategoryEnum;
  name: string;
  code: string;
  description: string;
  defaultPrivacyLevel: PiiLevel;
  mrzSupported: boolean;
}

export interface ExtractionRuleDto {
  id: string;
  fieldName: string;
  anchorKeywords: string[];
  regexPattern?: string;
  piiLevel: PiiLevel;
  requiresRedaction: boolean;
}

export interface ValidationRuleDto {
  id: string;
  name: string;
  algorithm: ValidationAlgorithm;
  targetField: string;
  parameters?: Record<string, JsonPrimitive>;
  errorMessage: string;
}

export interface DocumentSchemaDto {
  id: string;
  documentCategory: DocumentCategoryEnum;
  countryIso2: string;
  version: number;
  extractionRules: ExtractionRuleDto[];
  validationRules: ValidationRuleDto[];
  isActive: boolean;
}

export interface DocumentTemplateDto {
  id: string;
  schemaId: string;
  name: string;
  layoutAnchors: Array<{ keyword: string; x: number; y: number; width: number; height: number }>;
}

export interface PiiProfileDto {
  id: string;
  name: string;
  framework: RedactionFramework;
  level: PiiLevel;
  maskedFields: string[];
}

export interface RedactionPolicyDto {
  id: string;
  tenantId: string;
  name: string;
  framework: RedactionFramework;
  rules: Array<{ entityType: string; piiLevel: PiiLevel; redactionStrategy: 'BLACK_BOX' | 'PIXELATE' | 'BLUR' | 'HASH' }>;
}

export interface CompliancePolicyDto {
  id: string;
  framework: RedactionFramework;
  retentionDays: number;
  auditLoggingRequired: boolean;
  autoPurgeRawText: boolean;
}

export interface StandardizedResponse<T> {
  provider: string;
  model: string;
  latencyMs: number;
  costUsd: number;
  promptTokens: number;
  completionTokens: number;
  data: T;
  rawResponse?: string;
}

export interface ExtractionResultDto {
  documentId: string;
  fields: Record<string, {
    value: string;
    confidence: number;
    validationStatus: 'VALID' | 'INVALID' | 'UNCHECKED';
    validationAlgorithm?: ValidationAlgorithm;
    piiLevel: PiiLevel;
    page: number;
    boundingBox?: number[];
  }>;
  rawText: string;
  language: string;
  detectedCountryIso: string;
  detectedCategory: DocumentCategoryEnum;
}

export interface VerificationResultDto {
  ruleId: string;
  fieldName: string;
  algorithm: ValidationAlgorithm;
  isValid: boolean;
  normalizedValue: string;
  message?: string;
}

export interface ReviewTaskDto {
  id: string;
  documentId: string;
  tenantId: string;
  assignedTo?: string;
  status: ReviewTaskStatus;
  lowConfidenceFields: string[];
  overallConfidence: number;
  extractedFields: Record<string, FieldValueType>;
  createdAt: string | Date;
  updatedAt?: string | Date;
}

export interface DetectionResultDto {
  id?: string;
  documentId?: string;
  entityType?: string;
  name?: string;
  type?: string;
  entityValue?: string;
  value?: string;
  confidence?: number;
  validated?: boolean;
  validationAlgorithm?: string;
  privacyRegulation?: string;
  pii_level?: PiiLevel | string;
  startChar?: number;
  endChar?: number;
  page?: number;
  pageNumber?: number;
  bbox?: number[];
  boundingBox?: number[];
  isRedacted?: boolean;
}

export interface CountryIntelligenceDto {
  countryIso?: string;
  detectedCountryIso?: string | null;
  countryName: string;
  documentCategory?: string;
  privacyRegulations?: string[];
  confidence?: number;
  reasoning?: string;
}

export interface DocumentDto {
  id: string;
  tenantId: string;
  userId?: string;
  userEmail?: string;
  originalName: string;
  fileName?: string;
  fileSize?: number;
  mimeType: string;
  storagePath: string;
  redactedStoragePath?: string;
  status: string;
  countryIso?: string;
  countryName?: string;
  privacyRegulation?: string;
  documentCategory?: string;
  detectedDocumentType?: string;
  language?: string;
  pageCount?: number;
  rawText?: string;
  entityCount?: number;
  processedAt?: string | Date;
  createdAt: string | Date;
  updatedAt?: string | Date;
}

export interface DocumentProcessResultDto {
  document: DocumentDto;
  countryIntelligence: CountryIntelligenceDto;
  documentClassification?: Record<string, string | number | boolean>;
  extraction?: {
    language?: string;
    pageCount?: number;
    text?: string;
  };
  fields?: Record<string, FieldValueType | Record<string, FieldValueType>>;
  detections: DetectionResultDto[];
  humanReviewRequired: boolean;
  audit?: MetadataMap;
}

export interface DocumentDetailsDto {
  document: DocumentDto;
  detections: DetectionResultDto[];
  countryIntelligence?: CountryIntelligenceDto;
  reviewStatus?: string;
}

export interface RedactRequestDto {
  items: DetectionResultDto[];
  mode?: string;
}

export interface RedactResponseDto {
  message: string;
  document?: DocumentDto;
  redactionsCount?: number;
  unresolvedCount?: number;
  unresolvedEntities?: string[];
}

export interface CustomRuleDto {
  name: string;
  pattern: string;
  entityType: string;
}

export interface PolicyDto {
  id: string;
  tenantId: string;
  name: string;
  documentType?: string;
  rules: string[];
  customRules?: CustomRuleDto[];
  isDefault: boolean;
  createdAt: string | Date;
  updatedAt?: string | Date;
}

export interface CreatePolicyDto {
  name: string;
  documentType?: string;
  rules?: string[];
  customRules?: CustomRuleDto[];
  isDefault?: boolean;
}

export interface UpdatePolicyDto {
  name?: string;
  documentType?: string;
  rules?: string[];
  customRules?: CustomRuleDto[];
  isDefault?: boolean;
}

export interface ProviderDto {
  id: string;
  name: string;
  slug: string;
  defaultModel: string;
  baseUrl?: string;
  apiKey?: string;
  apiKeyMasked?: string;
  hasApiKey?: boolean;
  priority: number;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface CreateProviderDto {
  name: string;
  slug: string;
  defaultModel: string;
  apiKey?: string;
  baseUrl?: string;
  priority?: number;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface UpdateProviderDto {
  name?: string;
  slug?: string;
  defaultModel?: string;
  baseUrl?: string;
  apiKey?: string;
  priority?: number;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface TestConnectionResultDto {
  id?: string;
  status: 'SUCCESS' | 'FAILED' | string;
  latencyMs: number;
  message: string;
}

export interface AuditLogDto {
  id?: string;
  audit_id?: string;
  tenantId?: string;
  tenant_id?: string;
  userId?: string;
  user_id?: string;
  userEmail?: string;
  action?: string;
  documentId?: string;
  document_id?: string;
  ipAddress?: string;
  metadata?: MetadataMap;
  timestamp?: string | number | Date;
  iso_time?: string;
  provider?: string;
  model?: string;
}

export interface TenantDto {
  id: string;
  name: string;
  slug: string;
  createdAt: string | Date;
}

export interface CreateTenantDto {
  name: string;
  slug: string;
}

export interface CountryProfileDto {
  isoCode: string;
  name: string;
  code?: string;
  region?: string;
  languages?: string[];
  ocrLanguagePacks?: string[];
  rules?: string[];
  supportedDocuments?: DocumentCategoryEnum[];
}

export interface CountryProfilesResponseDto {
  count?: number;
  totalProfiles?: number;
  profiles: CountryProfileDto[];
}

export interface PendingReviewItemDto {
  id?: string;
  doc_id?: string;
  documentId?: string;
  fileName?: string;
  tenantId?: string;
  detectedType?: string;
  extractedFields?: Record<string, FieldValueType>;
  status: string;
  submittedAt?: string | Date;
  low_confidence_fields?: string[];
  overall_confidence?: number;
  history?: Array<Record<string, FieldValueType>>;
}

export interface ApproveReviewResponseDto {
  status: string;
  message?: string;
  reviewItem?: PendingReviewItemDto;
  review_item?: PendingReviewItemDto;
}

export interface CorrectReviewResponseDto {
  status: string;
  message?: string;
  reviewItem?: PendingReviewItemDto;
  review_item?: PendingReviewItemDto;
}

export interface AuditTrailResponseDto {
  documentId: string;
  logs: AuditLogDto[];
}

export interface PendingReviewsResponseDto {
  pending_reviews: PendingReviewItemDto[];
}

